import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { ContractWizardHeader } from "./ContractWizardHeader";
import { ContractWizardFooter } from "./ContractWizardFooter";
import { ContractWizardSteps } from "./ContractWizardSteps";
import { discountsDataToRequests } from "./discountPayload";
import type {
  ServiceDetail,
  PayerRequest,
  CreateContractRequest,
  ContractFamilyGroupDraft,
} from "@/types/billing";
// Value import of the generated enum, via the model module rather than the `@/api`
// barrel: tests that replace the barrel wholesale with `vi.mock('@/api', ...)` do not
// intercept a separate module id.
import { billing_PayerRequest } from "@/api/models/billing_PayerRequest";
import { useCreateContract, useBillingSettings } from "@/hooks/useBilling";
import { useChildRelationships } from "@/hooks/useChildContactRelationships";
import { useAllContacts } from "@/hooks/useContacts";
import {
  useAddFamilyGroupMembersBestEffort,
  useCreateFamilyGroup,
} from "@/hooks/useFamilyGroups";
import { RelationshipType } from "@/types/childContactRelationship";
import { getContractUpdateErrorMessage } from "@/utils/contractErrorMessages";
import { toast } from "sonner";

interface ContractWizardProps {
  onClose: () => void;
  /** Called with the new contract and its child, for the next enrolment step. */
  onSuccess?: (contractId: string, created: { childId: string; childName?: string }) => void;
  triggerRef?: React.RefObject<HTMLElement>;
  /** Child preselected on the first step, e.g. from "Create contract" on the child page. */
  initialChildId?: string;
}

interface MultiGroupServicePattern {
  [groupType: string]: Record<string, ServiceDetail[]>;
}

interface TariffVersions {
  [groupType: string]: string;
}

type FamilyGroup = ContractFamilyGroupDraft;

// Removed ensurePrimaryPayer function - primary payer no longer needed

interface WizardData {
  childId: string;
  childcareId?: string;
  startDate: string;
  endDate?: string;
  subsidised?: boolean;
  subsidyAmount?: number;
  /** On-top Subventionszuschuss in Rappen (billed to the office; never reduces the parent). */
  subsidySupplementAmount?: number;
  subsidyOfficeId?: string;
  beitragsfaktor?: number; // 0 ≤ BF ≤ 1 (BF=0 → city covers 100% of subsidisable portion)
  beitragsfaktorValidUntil?: string; // YYYY-MM-DD
  siblingDiscountManual?: boolean;
  subsidyDecisionPending?: boolean;
  servicePattern: MultiGroupServicePattern;
  payers: PayerRequest[];
  notes?: string;
  tariffVersions?: TariffVersions;
  billingPeriodOption?: "same" | "service_based" | "custom";
  billingStartDate?: string;
  billingEndDate?: string;
  discounts?: {
    weekday?: { [day: number]: number };
    custom?: Array<{
      id?: string;
      name: string;
      type: "percentage" | "amount";
      value: number;
    }>;
  };
  familyGroup?: FamilyGroup;
}

const STEPS = [
  "child",
  "details",
  "schedule",
  "discounts",
  "payers",
  "review",
] as const;
type Step = (typeof STEPS)[number];

export function ContractWizard({
  onClose,
  onSuccess,
  triggerRef,
  initialChildId,
}: ContractWizardProps) {
  const { i18n } = useLingui();
  const [currentStep, setCurrentStep] = useState<Step>("child");
  const [wizardData, setWizardData] = useState<Partial<WizardData>>({
    ...(initialChildId ? { childId: initialChildId } : {}),
    payers: [], // Initialize with empty array to ensure consistent state
    billingPeriodOption: "same", // Default to same as contract duration
  });
  const createContract = useCreateContract();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousStepRef = useRef<Step>(currentStep);
  const stepAnnouncementRef = useRef<HTMLDivElement>(null);

  // Fetch billing settings to get services
  const { data: billingSettings } = useBillingSettings();
  const services = billingSettings?.services || [];

  // Fetch relationships for the selected child
  const { data: relationships, isLoading: relationshipsLoading } =
    useChildRelationships(wizardData.childId || "", !!wizardData.childId);

  // Fetch all contacts to get full Contact objects with avatarUrl
  const { data: allContacts = [] } = useAllContacts();

  // Filter contacts to only show those that have a relationship with the selected child.
  // Falls back to all contacts if the child has no relationship records.
  const childContacts = React.useMemo(() => {
    if (!wizardData.childId) {
      return [];
    }

    if (relationshipsLoading) {
      return [];
    }

    // If no relationships exist, fall back to all contacts
    if (!relationships || relationships.length === 0) {
      return allContacts;
    }

    const contactIds = new Set<string | undefined>(
      relationships.map((rel) => rel.contactId)
    );
    return allContacts.filter((contact) => contactIds.has(contact.id));
  }, [relationships, relationshipsLoading, wizardData.childId, allContacts]);

  // Reset payers when child selection changes
  useEffect(() => {
    if (
      wizardData.childId &&
      wizardData.payers &&
      wizardData.payers.length > 0
    ) {
      // Check if current payers are still valid for the new child
      const validContactIds = new Set(childContacts.map((c) => c.id));

      // Only remove payers that have a non-empty contact_id that's not in the valid list
      // Keep payers with empty contact_id so user can see them and select a contact
      const invalidPayers = wizardData.payers.filter(
        (p) => p.contact_id && !validContactIds.has(p.contact_id)
      );

      // If any payers have invalid (non-empty) contact_ids, remove only those
      if (invalidPayers.length > 0) {
        const updatedPayers = wizardData.payers.filter(
          (p) => !p.contact_id || validContactIds.has(p.contact_id)
        );
        setWizardData((prev) => ({ ...prev, payers: updatedPayers }));
      }
    }
  }, [wizardData.childId, childContacts, wizardData.payers]);

  const stepIndex = STEPS.indexOf(currentStep);
  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  // Define canNavigateNext with useCallback but without circular dependencies
  const canNavigateNext = useCallback((): boolean => {
    switch (currentStep) {
      case "child":
        return !!wizardData.childId;
      case "details":
        return !!wizardData.startDate;
      case "schedule":
        // Check if at least one group has services selected
        return wizardData.servicePattern
          ? Object.values(wizardData.servicePattern).some((pattern) =>
              Object.values(pattern).some(
                (services) => services && services.length > 0
              )
            )
          : false;
      case "discounts":
        // Discounts are optional, always allow navigation
        return true;
      case "payers": {
        // If family group is configured, validate it
        if (wizardData.familyGroup) {
          // An existing family account was chosen: it is used as it is, so
          // its name, billing type and members need no validation here.
          if (wizardData.familyGroup.id) {
            return true;
          }
          const hasFamilyName = !!wizardData.familyGroup.familyName;

          if (wizardData.familyGroup.billingType === "SPLIT") {
            const hasMembers = wizardData.familyGroup.members.length > 0;
            const totalPercentage = wizardData.familyGroup.members.reduce(
              (sum, m) => sum + (m.paymentPercentage || 0),
              0
            );
            return hasFamilyName && hasMembers && totalPercentage === 100;
          }

          // For UNIFIED billing, only the family name is required. The
          // optional "Billing Email" field on the family group is just an
          // override — when empty (the normal case), invoice emails are
          // resolved live from the family group's PARENT member contacts at
          // send time (see invoiceEmailService.resolveRecipients). No need
          // to gate Next on it.
          return hasFamilyName;
        }

        // Otherwise validate individual payers
        const hasPayersData =
          !!wizardData.payers && wizardData.payers.length > 0;
        const allHaveContacts =
          wizardData.payers?.every((p) => {
            const hasContact = p.contact_id && p.contact_id !== "";
            return hasContact;
          }) || false;
        const percentageSum =
          wizardData.payers?.reduce(
            (sum, p) => sum + (p.percentage_share || 0),
            0
          ) || 0;
        const percentagesValid = percentageSum === 100;

        return hasPayersData && allHaveContacts && percentagesValid;
      }
      case "review":
        return true;
      default:
        return false;
    }
  }, [currentStep, wizardData]);

  // Announce step changes to screen readers
  useEffect(() => {
    if (previousStepRef.current !== currentStep) {
      const stepNames = {
        child: i18n._(msg`Select Child`),
        details: i18n._(msg`Contract Details`),
        schedule: i18n._(msg`Schedule`),
        discounts: i18n._(msg`Discounts`),
        payers: i18n._(msg`Payers`),
        review: i18n._(msg`Review`),
      };

      if (stepAnnouncementRef.current) {
        stepAnnouncementRef.current.textContent = i18n._(
          msg`Step ${stepIndex + 1} of ${STEPS.length}: ${
            stepNames[currentStep]
          }`
        );
      }
      previousStepRef.current = currentStep;
    }
  }, [currentStep, stepIndex, i18n]);

  // Focus management - restore focus when closing
  useEffect(() => {
    const triggerElement = triggerRef?.current;
    return () => {
      if (triggerElement) {
        triggerElement.focus();
      }
    };
  }, [triggerRef]);

  // Silent: the family group is one step of creating the contract, whose hook
  // shows the one success toast.
  const createFamilyGroup = useCreateFamilyGroup({ silent: true });
  const addFamilyGroupMembers = useAddFamilyGroupMembersBestEffort();

  // Best-effort, after the contract exists: the child's parents who are not
  // members of the chosen existing family account join it. A failure is
  // reported and never undoes the contract.
  const addMissingParentsToFamilyGroup = useCallback(
    async (groupId: string, group: FamilyGroup) => {
      const memberIds = new Set(group.members.map((m) => m.contactId));
      const parentIds = Array.from(
        new Set(
          (relationships ?? [])
            .filter(
              (rel) =>
                rel.relationshipType === RelationshipType.PARENT ||
                rel.relationshipType === RelationshipType.STEP_PARENT
            )
            .map((rel) => rel.contactId)
            .filter((id): id is string => !!id && !memberIds.has(id))
        )
      );
      if (parentIds.length === 0) return;
      const { failed, skippedForSplit } = await addFamilyGroupMembers(
        groupId,
        parentIds,
        group.billingType
      );
      if (skippedForSplit.length > 0) {
        toast.warning(
          i18n._(
            msg({
              id: "contracts.familyAccount.addParentsSplitSkipped",
              message:
                "The contract was created. The family account splits its invoices by percentage, so the parents were not added automatically. Add them to the family account's split by hand.",
            })
          )
        );
      }
      if (failed.length > 0) {
        toast.warning(
          i18n._(
            msg({
              id: "contracts.familyAccount.addParentsFailed",
              message:
                "The contract was created, but not all parents could be added to the family account. Add them on the family account.",
            })
          )
        );
      }
    },
    [relationships, addFamilyGroupMembers, i18n]
  );

  const handleSubmit = useCallback(async () => {
    if (
      !wizardData.childId ||
      !wizardData.startDate ||
      !wizardData.servicePattern ||
      !wizardData.payers
    ) {
      toast.error(i18n._(msg`Please complete all required fields`));
      return;
    }

    // A subsidy decision still pending relaxes the office/BF/amount
    // requirements (mirrors the edit path) — the office has not issued its
    // decision yet, so neither figure is known. The invoice is withheld until
    // the hold is cleared.
    if (wizardData.subsidised && !wizardData.subsidyDecisionPending) {
      const hasOffice =
        typeof wizardData.subsidyOfficeId === "string" &&
        wizardData.subsidyOfficeId.length > 0;
      const hasBf =
        typeof wizardData.beitragsfaktor === "number" &&
        wizardData.beitragsfaktor >= 0 &&
        wizardData.beitragsfaktor <= 1;
      const hasSubsidyAmount =
        typeof wizardData.subsidyAmount === "number" &&
        wizardData.subsidyAmount > 0;
      // beitragsfaktorValidUntil is optional — null means "never expires"
      if (!hasOffice) {
        toast.error(
          i18n._(
            msg({
              id: "validation.contract.subsidyOfficeRequired",
              message: "Subsidy office is required when subsidised",
            })
          )
        );
        return;
      }
      if (!hasBf && !hasSubsidyAmount) {
        toast.error(
          i18n._(
            msg({
              id: "validation.contract.subsidyAmountOrBfRequired",
              message:
                "Subsidised contracts require either a subsidy amount or a Beitragsfaktor (0 ≤ BF ≤ 1)",
            })
          )
        );
        return;
      }
    }

    // An on-top Subventionszuschuss requires a subsidy office to route the
    // grant to — independent of `subsidised`.
    if (
      typeof wizardData.subsidySupplementAmount === "number" &&
      wizardData.subsidySupplementAmount > 0
    ) {
      const hasOffice =
        typeof wizardData.subsidyOfficeId === "string" &&
        wizardData.subsidyOfficeId.length > 0;
      if (!hasOffice) {
        toast.error(
          i18n._(
            msg({
              id: "validation.contract.supplementAmountRequiresOffice",
              message:
                "A subsidy office is required when a Subventionszuschuss is set.",
            })
          )
        );
        return;
      }
    }

    let familyGroupId: string | undefined;
    // An existing family account chosen on the payers step (#2261): bill to
    // it instead of creating another family group for the same parents.
    const existingFamilyGroupId = wizardData.familyGroup?.id;

    if (existingFamilyGroupId) {
      familyGroupId = existingFamilyGroupId;
    } else if (wizardData.familyGroup && wizardData.childId) {
      // Create family group if configured
      try {
        const trimmedEmail = wizardData.familyGroup.unifiedBillingEmail?.trim();
        const familyGroupData = {
          child_id: wizardData.childId,
          family_name: wizardData.familyGroup.familyName,
          billing_type: wizardData.familyGroup.billingType,
          unified_billing_email: trimmedEmail ? trimmedEmail : undefined,
          members: wizardData.familyGroup.members.map((m) => ({
            contact_id: m.contactId,
            payment_percentage: m.paymentPercentage,
            can_view_invoices: true,
            can_make_payments:
              wizardData.familyGroup!.billingType === "UNIFIED",
            receives_separate_invoice:
              wizardData.familyGroup!.billingType === "SPLIT",
          })),
        };

        const result = await createFamilyGroup.mutateAsync(familyGroupData);
        familyGroupId = result.id;
      } catch (error) {
        // If the API for family groups is not available (404), proceed without creating one
        // to avoid blocking contract creation. For any other failure, abort — otherwise
        // the contract POST proceeds with an empty payer payload and trips its own
        // (confusing) validation error downstream.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err: any = error;
        const status = err?.response?.status || err?.status;
        console.error("Failed to create family group:", error);
        if (status === 404) {
          toast.message(
            i18n._(
              msg`Family groups not supported on this server. Continuing without a family group.`
            )
          );
        } else {
          toast.error(i18n._(msg`Failed to create family group`));
          return;
        }
      }
    }

    // Convert multi-group service pattern to backend format
    // Backend expects a flat service pattern with arrays of service codes per day
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const servicePattern: any = {};
    Object.entries(wizardData.servicePattern).forEach(([_, groupPattern]) => {
      Object.entries(groupPattern).forEach(([day, services]) => {
        if (!servicePattern[day]) {
          servicePattern[day] = [] as string[];
        }

        services.forEach((service: unknown) => {
          // Extract a code from various possible shapes
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const s = service as any;
          const code: string | undefined =
            s?.code || s?.serviceCode || s?.service_code || s?.service_id;
          if (code && !servicePattern[day].includes(code)) {
            servicePattern[day].push(code);
          }
        });
      });
    });

    // Build contracted_services in structured format
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const contractedServices: any[] = [];
    Object.entries(wizardData.servicePattern).forEach(
      ([groupType, groupPattern]) => {
        Object.entries(groupPattern).forEach(([day, services]) => {
          const weekdayNum =
            [
              "monday",
              "tuesday",
              "wednesday",
              "thursday",
              "friday",
              "saturday",
              "sunday",
            ].indexOf(day.toLowerCase()) + 1;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          services.forEach((service: any) => {
            const code =
              service?.code ||
              service?.serviceCode ||
              service?.service_code ||
              service?.service_id;
            if (code && weekdayNum > 0) {
              contractedServices.push({
                group_type: groupType || "STANDARD",
                weekday: weekdayNum,
                service: code,
              });
            }
          });
        });
      }
    );

    // Convert tariff versions from object to array format
    const tariffVersionsArray = wizardData.tariffVersions
      ? Object.entries(wizardData.tariffVersions)
          .filter(([_, versionId]) => versionId && versionId !== "none")
          .map(([groupType, tariffVersionId]) => ({
            group_type: groupType,
            tariff_version_id: tariffVersionId,
          }))
      : [];

    const discountsArray = discountsDataToRequests(wizardData.discounts);

    // Ensure we always have a payer when family billing is selected. The
    // synthesised fallback omits the optional `contact_id` (destructuring a
    // missing key already yields undefined at runtime) and names the wire enum
    // member so both branches keep the same `PayerRequest` element type; the
    // member's value is the identical "FAMILY_GROUP" string.
    const normalizedPayers: PayerRequest[] =
      wizardData.payers && wizardData.payers.length > 0
        ? wizardData.payers
        : familyGroupId
        ? [
            {
              payer_type: billing_PayerRequest.payer_type.FAMILY_GROUP,
              percentage_share: 100,
            },
          ]
        : [];

    // Add family_group_id to payers if a family group was created and set payer_type accordingly
    const payersWithFamilyGroup = normalizedPayers.map((payer) => {
      const hasFamilyGroup = !!familyGroupId;

      // Build payer object based on payer_type
      if (hasFamilyGroup) {
        // For FAMILY_GROUP, only include family_group_id, NOT contact_id
        const { contact_id: _contact_id, ...payerWithoutContactId } = payer;
        return {
          ...payerWithoutContactId,
          family_group_id: familyGroupId,
          payer_type: "FAMILY_GROUP",
        } as PayerRequest & { family_group_id?: string; payer_type?: string };
      } else {
        // For CONTACT, only include contact_id, NOT family_group_id
        return {
          ...payer,
          payer_type: "CONTACT",
        } as PayerRequest & { payer_type?: string };
      }
    });

    // Build the request with dates as FlexibleDate objects
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const request: CreateContractRequest & { discounts?: any[] } = {
      child_id: wizardData.childId!,
      start_date: { time: `${wizardData.startDate}T00:00:00Z` },
      end_date: wizardData.endDate
        ? { time: `${wizardData.endDate}T00:00:00Z` }
        : undefined,
      billing_start_date: wizardData.billingStartDate
        ? { time: `${wizardData.billingStartDate}T00:00:00Z` }
        : undefined,
      billing_end_date: wizardData.billingEndDate
        ? { time: `${wizardData.billingEndDate}T00:00:00Z` }
        : undefined,
      subsidised: wizardData.subsidised,
      sibling_discount_manual: wizardData.siblingDiscountManual ?? false,
      subsidy_decision_pending: wizardData.subsidyDecisionPending ?? false,
      subsidy_amount: wizardData.subsidyAmount,
      // On-top Subventionszuschuss (independent of `subsidised`; billed to the office).
      subsidy_supplement_amount: wizardData.subsidySupplementAmount,
      // The subsidy office is needed whenever the contract is subsidised OR an
      // office was picked (a Subventionszuschuss routes the grant to it). The
      // Beitragsfaktor fields are subsidised-only. When no office is selected,
      // forward nothing so toggling off leaves no stale values.
      ...(wizardData.subsidyOfficeId
        ? { subsidy_office_id: wizardData.subsidyOfficeId }
        : {}),
      ...(wizardData.subsidised
        ? {
            beitragsfaktor: wizardData.beitragsfaktor,
            beitragsfaktor_valid_until: wizardData.beitragsfaktorValidUntil
              ? { time: `${wizardData.beitragsfaktorValidUntil}T00:00:00Z` }
              : undefined,
          }
        : {}),
      service_pattern: servicePattern,
      contracted_services: contractedServices,
      payers: payersWithFamilyGroup,
      notes: wizardData.notes,
      tariff_versions: tariffVersionsArray,
      discounts: discountsArray.length > 0 ? discountsArray : undefined,
    };

    try {
      const result = await createContract.mutateAsync(request);
      if (result?.id && existingFamilyGroupId && wizardData.familyGroup) {
        await addMissingParentsToFamilyGroup(
          existingFamilyGroupId,
          wizardData.familyGroup,
        );
      }
      if (result?.id) {
        onSuccess?.(result.id, {
          childId: wizardData.childId!,
          childName: (result as { childName?: string }).childName,
        });
        onClose();
      }
    } catch (error: unknown) {
      // Route through the shared mapper so subsidy field-level 400s (e.g.
      // office-ownership) surface a localized message instead of the raw
      // "Validation failed".
      const errorMessage = getContractUpdateErrorMessage(
        error,
        i18n,
        i18n._(msg`Failed to create contract`),
      );
      toast.error(errorMessage);
    }
  }, [
    wizardData,
    createContract,
    createFamilyGroup,
    addMissingParentsToFamilyGroup,
    onSuccess,
    onClose,
    i18n,
  ]);

  const handleNext = useCallback(() => {
    if (currentStep === "review") {
      handleSubmit();
    } else {
      const nextIndex = Math.min(stepIndex + 1, STEPS.length - 1);
      const nextStep = STEPS[nextIndex];
      setCurrentStep(nextStep);
    }
  }, [currentStep, stepIndex, handleSubmit]);

  const handleBack = useCallback(() => {
    const prevIndex = Math.max(stepIndex - 1, 0);
    setCurrentStep(STEPS[prevIndex]);
  }, [stepIndex]);

  const handleUpdateData = useCallback((updates: Partial<WizardData>) => {
    setWizardData((prev) => {
      const next = { ...prev, ...updates };

      if (updates.familyGroup) {
        next.familyGroup = updates.familyGroup;
      }

      if (updates.payers) {
        next.payers = updates.payers;
      }

      return next;
    });
  }, []);

  // Keyboard shortcuts handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Escape key to close
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      // Alt+N for Next (when not on last step)
      if (
        event.altKey &&
        event.key === "n" &&
        stepIndex < STEPS.length - 1 &&
        canNavigateNext()
      ) {
        event.preventDefault();
        handleNext();
        return;
      }

      // Alt+B for Back (when not on first step)
      if (event.altKey && event.key === "b" && stepIndex > 0) {
        event.preventDefault();
        handleBack();
        return;
      }
    },
    [stepIndex, handleNext, handleBack, onClose, canNavigateNext]
  );

  const stepTitles = {
    child: i18n._(msg`Select Child`),
    details: i18n._(msg`Contract Details`),
    schedule: i18n._(msg`Schedule`),
    discounts: i18n._(msg`Discounts`),
    payers: i18n._(msg`Payers`),
    review: i18n._(msg`Review`),
  };

  return (
    <div
      ref={dialogRef}
      className="flex flex-col h-full"
      role="dialog"
      aria-modal="true"
      aria-label={i18n._(msg`Contract Creation Wizard`)}
      aria-describedby="wizard-description"
      onKeyDown={handleKeyDown}
    >
      {/* Screen reader announcements */}
      <div
        ref={stepAnnouncementRef}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      <span id="wizard-description" className="sr-only">
        {i18n._(
          msg`A multi-step wizard to create a new contract. Use Alt+N to go to next step, Alt+B to go back, and Escape to close.`
        )}
      </span>

      <ContractWizardHeader
        currentStep={currentStep}
        stepIndex={stepIndex}
        totalSteps={STEPS.length}
        progress={progress}
        onClose={onClose}
        stepTitle={stepTitles[currentStep]}
      />

      <ContractWizardSteps
        currentStep={currentStep}
        wizardData={wizardData}
        onUpdateData={handleUpdateData}
        stepIndex={stepIndex}
        contacts={childContacts}
        services={services}
      />

      <ContractWizardFooter
        currentStep={currentStep}
        stepIndex={stepIndex}
        canNavigateNext={canNavigateNext()}
        isSubmitting={createContract.isPending}
        onBack={handleBack}
        onNext={handleNext}
        onCancel={onClose}
      />
    </div>
  );
}
